import React, { useState, useEffect, useCallback } from 'react';
import {
  FormControl,
  FormLabel,
  FormControlLabel,
  TextField,
  Radio,
  RadioGroup,
  FormHelperText,
  Grid,
  Select,
  MenuItem
} from '@mui/material';
import useStyles from '../useStyles';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useDebounce } from 'use-debounce';
import { CircularProgress } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ScoutAutocomplete from '../../../scout/autocomplete';
import AdProductSelection from '../Components/AdProductSelection';
import PayDetails from '../Components/PayDetails';
import WorkTypes from '../Components/WorkTypes';
import SeekBillingReference from '../Components/SeekBillingReference';

const TITLE_LIMIT = 80;

function Step1({
  answers,
  setAnswers,
  apiKey,
  requiredRefs,
  activeError,
  jobAdId,
  setBrandingEnabled,
  setMaxBulletPoints,
  seek_billing_reference,
  seek_billing_reference_flag
}) {
  const [loadingSeekAdAllCategories, setLoadingSeekAdTypeAllCategories] = useState(true);
  const [loadingSeekAdCategories, setLoadingSeekAdCategories] = useState(true);
  const [seekAdCategories, setSeekAdCategories] = useState(null);
  const [getSeekAdCategoriesData, setGetSeekAdCategoriesData] = useState(null);
  const [categoriesIndex, setCategoriesIndex] = useState(null);
  const [debouncedTitle] = useDebounce(answers.title, 2500);
  const [debouncedAddress] = useDebounce(answers.listing_location, 2500);
  const classes = useStyles();

  const updateCategoryIndex = useCallback(
    (rawData) => {
      const data = rawData || getSeekAdCategoriesData;
      if (data) {
        const index = data.findIndex(
          (category) => category.id.value === answers.listing_classification
        );
        setCategoriesIndex(index);
      }
    },
    [answers.listing_classification, getSeekAdCategoriesData]
  );

  useEffect(() => {
    const getSeekAdCategories = async (url) => {
      setLoadingSeekAdTypeAllCategories(true);
      try {
        const { data } = await axios.get(url, {
          headers: { 'Content-Type': 'application/json' }
        });
        setGetSeekAdCategoriesData(data.data.jobCategories);
        setLoadingSeekAdTypeAllCategories(false);
      } catch (e) {
        setSeekAdCategories(null);
        setLoadingSeekAdTypeAllCategories(false);
      }
    };

    getSeekAdCategories(
      `/api/v4/seek/job_ads/${jobAdId}/job_categories?location_id=${answers.listing_location}`
    );
  }, [jobAdId, answers.listing_location]);

  useEffect(() => {
    answers.use_suggested_classification &&
      answers.listing_classification &&
      answers.listing_sub_classification &&
      updateCategoryIndex(getSeekAdCategoriesData);
  }, [
    answers.listing_classification,
    answers.listing_sub_classification,
    answers.use_suggested_classification,
    getSeekAdCategoriesData,
    updateCategoryIndex
  ]);

  const getSugestedSeekAdCategories = async (url) => {
    if (!answers.title || !answers.listing_location) {
      setSeekAdCategories(null);
      setLoadingSeekAdCategories(false);
      return;
    }

    try {
      const { data } = await axios.get(url, {
        headers: { 'X-api-authenticate': apiKey },
        params: { title: answers.title, listing_location: answers.listing_location }
      });
      setSeekAdCategories(data.data.jobCategorySuggestions);
      setLoadingSeekAdCategories(false);
    } catch (e) {
      setSeekAdCategories(null);
      setLoadingSeekAdCategories(false);
    }
  };

  useEffect(() => {
    getSugestedSeekAdCategories(`/api/v4/seek/job_ads/${jobAdId}/job_categories/suggestions`);
  }, [jobAdId, debouncedTitle, debouncedAddress]);

  useEffect(() => {
    setLoadingSeekAdCategories(true);
  }, [debouncedTitle, answers.listing_location]);

  useEffect(() => {
    if (!getSeekAdCategoriesData) return;
    updateCategoryIndex(null);
  }, [answers.listing_classification, getSeekAdCategoriesData, updateCategoryIndex]);

  return (
    <div className={classes.stepContainer}>
      <FormControl
        ref={requiredRefs.title}
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
      >
        <FormLabel required={true} className={classes.label}>
          Job Title
        </FormLabel>
        <TextField
          error={activeError === requiredRefs.title}
          className={classes.textField}
          variant="outlined"
          value={answers.title}
          onChange={(e) =>
            e.target.value.length <= TITLE_LIMIT &&
            setAnswers({ ...answers, title: e.target.value })
          }
          InputProps={{
            endAdornment: activeError === requiredRefs.title && (
              <div className={classes.error}>
                <ErrorIcon color="error" />
                <span>This field is required</span>
              </div>
            )
          }}
        />
        <FormHelperText className={classes.helperText}>
          {answers.title.length}/{TITLE_LIMIT}
        </FormHelperText>
      </FormControl>

      <FormControl
        ref={requiredRefs.listing_location}
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
      >
        <FormLabel className={classes.label}>
          Location
          <span className={classes.asterisk}> *</span>
          {activeError === requiredRefs.listing_location && (
            <div className={classes.error}>
              <ErrorIcon color="error" />
              <span id="SeekMultiposterErrorTxt">This field is required</span>
            </div>
          )}
        </FormLabel>
        <ScoutAutocomplete
          className={`${activeError === requiredRefs.listing_location ? classes.errorAutocomplete : ''} select-seek-location`}
          url={`/api/v4/seek/job_ads/${jobAdId}/locations/options`}
          name="location"
          value={answers.listing_location}
          selectedLabel={answers.listing_location_display}
          includeBlank={true}
          placeholder="Type to search..."
          onChange={(value, label, attrs) => {
            setAnswers({
              ...answers,
              listing_location: value,
              listing_location_display: label,
              currency: attrs && attrs.currencies ? attrs.currencies[0].code : 'AUD'
            });
          }}
        />
      </FormControl>

      <FormControl
        ref={requiredRefs.category}
        fullWidth={true}
        className={`${classes.formControl}`}
      >
        <FormLabel focused={false} className={classes.label}>
          Category
          <span className={classes.asterisk}> *</span>
          {activeError === requiredRefs.category && (
            <div className={classes.error}>
              <ErrorIcon color="error" />
              <span>This field is required</span>
            </div>
          )}
        </FormLabel>
        {seekAdCategories && (
          <span className={classes.subLabel}>
            Suggested category based on both job title and location
          </span>
        )}
        {loadingSeekAdCategories || loadingSeekAdAllCategories ? (
          <CircularProgress size={20} color="inherit" />
        ) : seekAdCategories ? (
          <RadioGroup
            value={answers.listing_sub_classification}
            onChange={(e) => {
              setAnswers({
                ...answers,
                listing_sub_classification: e.target.value,
                use_suggested_classification: false,
                listing_sub_classification_display: e.target.name
              });
            }}
          >
            {seekAdCategories.map((category) => (
              <div key={category.jobCategory.id.value}>
                <FormControlLabel
                  label={category.jobCategory.parent.name}
                  name={`${category.jobCategory.parent.name} > ${category.jobCategory.name}`}
                  value={category.jobCategory.id.value}
                  control={<Radio />}
                />
                <div>
                  <span style={{ padding: '0 0.5rem 0 1.75rem' }}>&gt;</span>{' '}
                  {category.jobCategory.name}
                </div>
              </div>
            ))}
          </RadioGroup>
        ) : (
          <Grid item>
            <div>
              {!answers.listing_location || !answers.listing_sub_classification
                ? 'No suggested categories, please add a job title and location to see suggested categories'
                : 'No suggested categories for the current job title and location'}
            </div>
          </Grid>
        )}
        <RadioGroup
          value={answers.use_suggested_classification}
          onChange={() =>
            setAnswers({
              ...answers,
              use_suggested_classification: true,
              listing_sub_classification: null,
              listing_classification: null
            })
          }
        >
          <FormControlLabel
            key={'other'}
            label={'Choose a different category'}
            checked={answers.use_suggested_classification === true}
            control={<Radio />}
          />
        </RadioGroup>
        {answers.use_suggested_classification && getSeekAdCategoriesData && (
          <FormControl
            style={{ marginTop: '5px' }}
            fullWidth={true}
            className={`${classes.formControl} ${classes.medium}`}
          >
            <Select
              error={activeError === requiredRefs.category}
              classes={{ select: classes.input }}
              variant="outlined"
              value={answers.listing_classification}
              onChange={(e) => {
                const index = getSeekAdCategoriesData.findIndex(
                  (category) => category.id.value === e.target.value
                );
                setAnswers({
                  ...answers,
                  listing_classification: e.target.value,
                  listing_classification_display:
                    index >= 0 ? getSeekAdCategoriesData[index].name : ''
                });
              }}
              displayEmpty={true}
            >
              {getSeekAdCategoriesData.map((category) => (
                <MenuItem key={category.id.value} value={category.id.value}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            <FormLabel
              focused={false}
              style={{ marginTop: '20px' }}
              required={true}
              className={classes.label}
            >
              Sub Category
            </FormLabel>
            <Select
              error={activeError === requiredRefs.category}
              classes={{ select: classes.input }}
              disabled={!answers.listing_classification}
              variant="outlined"
              value={answers.listing_sub_classification}
              onChange={(e) => {
                const index = getSeekAdCategoriesData[categoriesIndex].children.findIndex(
                  (category) => category.id.value === e.target.value
                );
                setAnswers({
                  ...answers,
                  listing_sub_classification: e.target.value,
                  listing_sub_classification_display:
                    index >= 0 ? getSeekAdCategoriesData[categoriesIndex].children[index].name : ''
                });
              }}
              displayEmpty={true}
            >
              {answers.listing_classification &&
                getSeekAdCategoriesData &&
                typeof categoriesIndex === 'number' &&
                categoriesIndex >= 0 &&
                getSeekAdCategoriesData[categoriesIndex].children.map((category) => (
                  <MenuItem key={category.id.value} value={category.id.value}>
                    {category.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
      </FormControl>

      <WorkTypes answers={answers} setAnswers={setAnswers} />
      <PayDetails
        answers={answers}
        setAnswers={setAnswers}
        requiredRefs={requiredRefs}
        activeError={activeError}
      />
      {seek_billing_reference && seek_billing_reference_flag && (
        <SeekBillingReference answers={answers} setAnswers={setAnswers} />
      )}

      <h4>SEEK Ad Type</h4>
      {activeError === requiredRefs.ad_type && (
        <div className={classes.error}>
          <ErrorIcon color="error" />
          <span id="SeekMultiposterErrorTxt">This field is required</span>
        </div>
      )}

      {answers.seek_account_id && (
        <AdProductSelection
          answers={answers}
          setAnswers={setAnswers}
          requiredRefs={requiredRefs}
          jobAdId={jobAdId}
          setMaxBulletPoints={setMaxBulletPoints}
          setBrandingEnabled={setBrandingEnabled}
          debouncedTitle={debouncedTitle}
        />
      )}
    </div>
  );
}

Step1.propTypes = {
  answers: PropTypes.object.isRequired,
  setAnswers: PropTypes.func.isRequired,
  apiKey: PropTypes.string.isRequired,
  requiredRefs: PropTypes.object.isRequired,
  activeError: PropTypes.ref,
  jobAdId: PropTypes.number.isRequired,
  seek_billing_reference: PropTypes.bool,
  seek_billing_reference_flag: PropTypes.bool,
  setBrandingEnabled: PropTypes.func.isRequired,
  setMaxBulletPoints: PropTypes.func.isRequired
};

export default Step1;
